import PropTypes from 'prop-types';
import classNames from 'classnames';
import { makeStyles } from '@material-ui/core';
import QuestionEntry from '../questionEntry';
import { prepareAnswerWeight } from '../../../utility/attribute';
import { ATTRIBUTES } from '../../../constants/attributes';
import ArchivedQuestion from '../archivedQuestion';
import ReorderItems from '../reorderItems';

const useStyles = makeStyles(({ palette: { primary }, spacing }) => ({
  questionsWrapper: {
    marginBottom: spacing(10),
  },
  archivedQuestions: {
    '& > div': {
      borderBottom: `1px solid ${primary.bluish5}`,
    },
    '& > div:last-of-type': {
      borderBottom: 'none',
    },
  },
}));

const DroppableAttributeQuestionsList = ({
  translations,
  attribute,
  questions,
  initialAttribute,
  onDragEnd,
  onDeleteQuestion,
  onUpdateQuestion,
  onDuplicateQuestion,
  onArchiveQuestion,
  onQuestionUnarchive,
  switchHorizontalWarning,
  isEditDisabled,
  isQuestionView,
  hasQuestions,
  hasError = {},
}) => {
  const classes = useStyles();
  const isVertical = attribute.layout === ATTRIBUTES.verticalLayout;
  const hasDefinedAnswers =
    !attribute[ATTRIBUTES.fields.withTextReplyQuestions];

  return (
    <>
      {!isQuestionView && (
        <div className={classes.archivedQuestions}>
          {attribute.questions.map((questionItem, index) => {
            const initialItem = initialAttribute.questions.find(
              q => q.unique_id === questionItem.unique_id
            );

            return (
              questionItem?.is_archived && (
                <ArchivedQuestion
                  key={`question_${questionItem.id}`}
                  translations={translations}
                  questionIndex={index}
                  item={questionItem}
                  initialItem={initialItem}
                  answerWeights={questionItem.answers?.map(answer =>
                    prepareAnswerWeight(answer.weight)
                  )}
                  isVertical={isVertical}
                  hasDefinedAnswers={hasDefinedAnswers}
                  isEditDisabled={isEditDisabled}
                  handleUnarchive={onQuestionUnarchive}
                />
              )
            );
          })}
        </div>
      )}
      {isQuestionView && (
        <div>
          <ReorderItems
            className={classNames({
              [classes.questionsWrapper]: hasQuestions,
            })}
            itemClass={classes.draggableItem}
            isDragDisabled={!attribute.is_editable}
            onSave={onDragEnd}
          >
            {questions.map((questionItem, index) => {
              return (
                !questionItem?.is_archived && (
                  <QuestionEntry
                    id={questionItem.unique_id}
                    key={`question_${questionItem.unique_id}`}
                    translations={translations}
                    questionIndex={index}
                    hasErrors={hasError?.[index]}
                    item={questionItem}
                    answerWeights={questionItem.answers?.map(answer =>
                      prepareAnswerWeight(answer.weight)
                    )}
                    disableActions={!attribute.is_editable}
                    isVertical={isVertical}
                    isOpenEndedAttribute={attribute?.text_reply_questions}
                    isEditDisabled={isEditDisabled}
                    hasDefinedAnswers={hasDefinedAnswers}
                    hasNeutralAnswer={attribute.with_neutral_answer}
                    onArchive={onArchiveQuestion}
                    onDuplicate={onDuplicateQuestion}
                    onDelete={onDeleteQuestion}
                    onSave={onUpdateQuestion}
                    switchHorizontalWarning={switchHorizontalWarning}
                  />
                )
              );
            })}
          </ReorderItems>
        </div>
      )}
    </>
  );
};

DroppableAttributeQuestionsList.propTypes = {
  translations: PropTypes.object.isRequired,
  attribute: PropTypes.object.isRequired,
  onDragEnd: PropTypes.func.isRequired,
  onDeleteQuestion: PropTypes.func.isRequired,
  onUpdateQuestion: PropTypes.func.isRequired,
  onDuplicateQuestion: PropTypes.func.isRequired,
  onArchiveQuestion: PropTypes.func.isRequired,
  switchHorizontalWarning: PropTypes.func.isRequired,
  onQuestionUnarchive: PropTypes.func.isRequired,
  isQuestionView: PropTypes.bool.isRequired,
  hasQuestions: PropTypes.bool.isRequired,
  isEditDisabled: PropTypes.bool.isRequired,
  hasError: PropTypes.object,
  initialAttribute: PropTypes.object.isRequired,
};

export default DroppableAttributeQuestionsList;
